import booleanIntersects from "@turf/boolean-intersects";
import apiConfig from "../config/api.config";
import { crops, forestry } from "../config/burns.constants";
import { layers } from "../config/map.constants";
import { getJson, saveJson } from "./offline/cache.service";
import { _postJson, _requestJson } from "./request.service"

/**
 * Gets all active prescribed burns in the database
 * @returns {null |
    [
        {
            "BurnInfo": {
                "StartDate": number;
                "Weather": {
                    "WindSpeed": number;
                    "CategoryDay": number;
                    "Humidity": number;
                },
                "EndDate": number;
                "FuelLoad": {
                    "TotalAcres": string;
                    "CropType": string;
		    "CropCategory": 'crop' | 'forestry';
                    "FuelLoadingRange": string;
                    "TotalTonnage": number;
                },
                "Comments": string;
            },
	    Loc: {
		    county: string,
		    coords: number[] // [long, lat]
	    }
        },
    ]
 */
export const getBurns = async () => {
	return (await _requestJson(`${apiConfig.url}/items`).then(b => b.Items).catch(() => null));
}

/**
 * Submits the burn information to the api. Converts dates to UTC before submitting.
 * @param {*} data 
 * * {
 *  info: {
 *    name: string;
 *    phone: string;
 *    startDate: number;
 *    endDate: number;
 *  }
 * location: {
 * 	county: string; //county name
 * 	coords: {longitude:number, latitude:number}
 * },
 * fuelLoad: {
	cropCategory: 'crop' | 'forestry',
*     cropType: string,
 *    fuelLoadingRange: // 0: Low, 1: Medium, 2: High
 *    totalAcres: number,
 *    totalTonnage: number
 * },
 * comments: string,
 * weather: {
 *  catDay: {value:string},
 *  windSpeed: {value:number},
 *  windDirection: {value:number},
 *  humidity: {value:number},
 * }
 * 
 * }
 */
export const submitBurnInfo = async (data) => {
	const {fuelLoadingRange, cropType} = data.fuelLoad;
	const crop = crops.find(c => c.name === cropType) || forestry.find(f => f.name === cropType);
	const tonsPerAcre = crop.fuelLoadingRange[fuelLoadingRange];

	return await _postJson(`${apiConfig.url}/items`, {
		...data,
		fuelLoad: {
			...data.fuelLoad,
			fuelLoadingRange: tonsPerAcre
		},
		info: {
			name: data.info.name,
			phone: data.info.phoneNum,
			startDate: new Date(data.info.startDate).toISOString(),
			endDate: new Date(data.info.endDate).toISOString()
		},
		weather: {
			catDay: data.weather.catDay,
			windSpeed: data.weather.windSpeed ? data.weather.windSpeed.value : 0,
			windDirection: data.weather.windDirection ? data.weather.windDirection.value : 0,
			humidity: data.weather.humidity ? data.weather.humidity.value : 0
		}
	});
}

export const fetchAndCacheBurns = async () => {
	const burns = await getBurns();
	saveJson('burns', burns);
}

/**
 * Retrieves nearby burnfeatures from local storage. Convert them to FeatureCollection to be displayed on a map.
 * @returns {{type: "FeatureCollection", features: Feature[]} | null}
 */
export const getCachedBurns = () => {
	const burns =  getJson('burns');
	if (!burns) return null;
	const features = burns.map((burn,i) => ({
		id: i,
		type: 'Feature',
		geometry: {
			type: 'Point',
			coordinates: burn.Loc.Coords
		},
		properties: burn
	}));
	
	return {
		type: 'FeatureCollection',
		features,
		layerId: layers.prescribed_burns.id
	}
}

/**
 * Filter burns that intersect with polygon 
 * @param {Array<Feature>} burns 
 * @param {Feature} polygon 
 * @returns 
 */
export const getBurnsInsidePolygon = (burns, polygon) => {
	return burns.filter(b => !!b.geometry.coordinates).filter(f => booleanIntersects(f, polygon));
}